<header class="editor-head">
  @if ($taskName()) {
    <span class="editor-eyebrow">{{ $taskName() }}</span>
  }
  <div class="editor-title-row">
    <h2 class="editor-title" [id]="titleId">Schedule</h2>
    <button class="editor-close" type="button" aria-label="Close" (click)="cancel()">
      <i class="fa-solid fa-xmark"></i>
    </button>
  </div>
  <wb-button-tab class="editor-kinds" [commands]="$kindOptions()" [activeCommandId]="$kind()" [fullWidth]="true" (onCommandChanged)="onKindChange($event.data)" />
</header>

<!-- The only scrolling region. Header and footer stay pinned however tall the pattern gets. -->
<div class="editor-body">

  @switch ($kind()) {

    @case ('perVisit') {
      <section class="editor-block">
        <div class="editor-radio-rows">
          <wb-button-radio label="Every visit" [checked]="$everyVisit()" [disabled]="$readonly()" (onToggle)="onEveryVisit(true)" />
          <div class="editor-radio-inline">
            <wb-button-radio label="Every Nth visit" [checked]="!$everyVisit()" [disabled]="$readonly()" (onToggle)="onEveryVisit(false)" />
            @if (!$everyVisit()) {
              <wb-number-stepper label="Visit interval" [value]="$draft().perVisit.nth" [min]="2" [disabled]="$readonly()" (valueChange)="onPerVisitNth($event)" />
            }
          </div>
        </div>
        <p class="editor-caption">Runs when another task on this line creates a visit</p>
        <!-- The one kind that creates nothing. Said before Save, not discovered afterwards. -->
        <p class="editor-note">{{ perVisitFootnote }}</p>
      </section>
    }

    @case ('afterService') {
      <section class="editor-block">
        <span class="editor-label">Due again after</span>
        <!-- Stepper, unit and caption stay on one line: the caption completes the sentence the controls
             start, so orphaning it on its own row breaks the reading order. -->
        <div class="editor-assembly">
          <wb-number-stepper label="Interval" [value]="$draft().afterService.interval" [min]="1" [disabled]="$readonly()" (valueChange)="onAfterServiceInterval($event)" />
          <wb-button-select class="editor-unit-select" [commands]="unitOptions" [value]="$draft().afterService.unit" [disabled]="$readonly()" (onCommandChanged)="onAfterServiceUnit($event.data)" />
          <span class="editor-caption">after each completed service</span>
        </div>
      </section>

      <!-- The compliance window [due − early, due + late]: work inside it counts, nothing breaches until it closes. -->
      <section class="editor-block">
        <span class="editor-label">Can be done up to</span>
        <div class="editor-assembly">
          <wb-number-stepper label="Early leeway" [value]="$draft().afterService.earlyLeeway.value" [min]="0" [disabled]="$readonly()" (valueChange)="onLeewayValue('earlyLeeway', $event)" />
          <wb-button-select class="editor-unit-select" [commands]="unitOptions" [value]="$draft().afterService.earlyLeeway.unit" [disabled]="$readonly()" (onCommandChanged)="onLeewayUnit('earlyLeeway', $event.data)" />
          <span class="editor-caption">early</span>
        </div>
        <div class="editor-assembly">
          <wb-number-stepper label="Late leeway" [value]="$draft().afterService.lateLeeway.value" [min]="0" [disabled]="$readonly()" (valueChange)="onLeewayValue('lateLeeway', $event)" />
          <wb-button-select class="editor-unit-select" [commands]="unitOptions" [value]="$draft().afterService.lateLeeway.unit" [disabled]="$readonly()" (onCommandChanged)="onLeewayUnit('lateLeeway', $event.data)" />
          <span class="editor-caption">late</span>
        </div>
      </section>

      <section class="editor-block">
        <span class="editor-label">Next due measured from</span>
        <div class="editor-assembly">
          <wb-button-select [commands]="anchorOptions" [value]="$draft().afterService.anchor" [disabled]="$readonly()" (onCommandChanged)="onAfterServiceAnchor($event.data)" />
        </div>
      </section>

      <!-- Replaces the old first-due input. An after-service task is due `interval` after its LAST
           service, so the honest question is when that happened; empty means the chain starts at the
           line.

           Live only. A definition has no service history to date: `lastCompleted` is one of the
           standing-task fields a `PlannedTask` on a TEMPLATE parent may never carry, so on a template
           the cadence stands alone. -->
      @if ($showLastServiced()) {
        <section class="editor-block">
          <span class="editor-label">Last serviced</span>
          <div class="editor-assembly">
            <button class="editor-datefield last-serviced" type="button" [class.placeholder]="!$hasLastServiced()" [disabled]="$readonly()" cdkOverlayOrigin #lastServicedOrigin="cdkOverlayOrigin" (click)="toggleLastServiced()">
              <i class="editor-datefield-icon fa-regular fa-calendar" aria-hidden="true"></i>
              <span class="editor-datefield-value">{{ $lastServicedLabel() }}</span>
              <i class="editor-datefield-caret fa-solid fa-chevron-down" aria-hidden="true"></i>
            </button>
            @if ($hasLastServiced() && !$readonly()) {
              <button class="editor-link" type="button" (click)="clearLastServiced()">Clear</button>
            }
            <span class="editor-caption">optional · empty means due at the line start</span>
          </div>

          <ng-template cdkConnectedOverlay [cdkConnectedOverlayOrigin]="lastServicedOrigin" [cdkConnectedOverlayPositions]="firstDuePositions" [cdkConnectedOverlayOpen]="$lastServicedOpen()" [cdkConnectedOverlayHasBackdrop]="false" [cdkConnectedOverlayPush]="true" (overlayOutsideClick)="closeLastServiced()" (detach)="closeLastServiced()">
            <div class="editor-datepopover">
              <wb-mini-calendar [value]="$draft().lastServiced ?? null" [disabledDate]="isFutureDate" (valueChange)="onLastServiced($event)" />
            </div>
          </ng-template>
        </section>
      }
    }

    @case ('calendar') {
      <section class="editor-block">
        <span class="editor-label">Repeat</span>
        <wb-button-chip class="editor-repeats" [commands]="$repeatOptions()" [value]="$repeatSelection()" [disabled]="$readonly()" (onCommandChanged)="onRepeatChange($event.data)" />
      </section>

      <!-- Straight after Repeat and before the pattern: the pattern is read from this date — Week A,
           quarterly months and the after-count end all start here, exactly as the server starts them.
           Calendar only — the other kinds have no start to override. -->
      @if ($showFirstDue()) {
        <section class="editor-block">
          <span class="editor-label">First due</span>
          <div class="editor-assembly">
            <button class="editor-datefield first-due" type="button" [disabled]="$readonly()" cdkOverlayOrigin #firstDueOrigin="cdkOverlayOrigin" (click)="toggleFirstDue()">
              <i class="editor-datefield-icon fa-regular fa-calendar" aria-hidden="true"></i>
              <span class="editor-datefield-value">{{ $firstDueDate() }}</span>
              @if ($firstDueHint()) {
                <span class="editor-datefield-hint">{{ $firstDueHint() }}</span>
              }
              <i class="editor-datefield-caret fa-solid fa-chevron-down" aria-hidden="true"></i>
            </button>
            @if ($hasStartOverride() && !$readonly()) {
              <button class="editor-link" type="button" (click)="clearFirstDue()">{{ withServiceLineLabel }}</button>
            }
          </div>

          <ng-template cdkConnectedOverlay [cdkConnectedOverlayOrigin]="firstDueOrigin" [cdkConnectedOverlayPositions]="firstDuePositions" [cdkConnectedOverlayOpen]="$firstDueOpen()" [cdkConnectedOverlayHasBackdrop]="false" [cdkConnectedOverlayPush]="true" (overlayOutsideClick)="closeFirstDue()" (detach)="closeFirstDue()">
            <div class="editor-datepopover">
              <wb-mini-calendar [value]="$draft().startsOn ?? null" [disabledDate]="isOutsideLine" (valueChange)="onFirstDue($event)" />
            </div>
          </ng-template>
        </section>
      }

      <section class="editor-block">
        <span class="editor-label">On</span>

        @if ($repeat() === 'daily') {
          <div class="editor-radio-rows">
            @for (preset of dailyPresets; track preset.preset) {
              <div class="editor-radio-inline">
                <wb-button-radio [label]="preset.label" [checked]="($calendar().dailyPreset ?? 'everyDay') === preset.preset" [disabled]="$readonly()" (onToggle)="onDailyPreset(preset.preset)" />
                @if (preset.preset === 'everyNDays' && ($calendar().dailyPreset ?? 'everyDay') === 'everyNDays') {
                  <wb-number-stepper label="Day interval" [value]="$calendar().dailyInterval ?? 2" [min]="2" [disabled]="$readonly()" (valueChange)="onDailyInterval($event)" />
                  <span class="editor-muted">days</span>
                }
              </div>
            }
          </div>
        }

        @if ($repeat() === 'weekly') {
          <div class="editor-inline">
            <span class="editor-muted">Every</span>
            <wb-number-stepper label="Week interval" [value]="$calendar().weeklyInterval ?? 1" [min]="1" [disabled]="$readonly()" (valueChange)="onWeeklyInterval($event)" />
            <span class="editor-muted">{{ ($calendar().weeklyInterval ?? 1) === 1 ? 'week' : 'weeks' }}</span>
          </div>
        }

        @if ($showWeekdayToggles()) {
          <div class="editor-day-toggles">
            @for (day of weekdayToggles; track day.weekday) {
              <wb-button-toggle displayStyle="Primary" [label]="day.label" [checked]="isWeekdaySelected(day.weekday)" [disabled]="$readonly()" (onCheck)="onWeekdayToggle(day.weekday)" />
            }
          </div>
        }

        @if ($repeat() === 'fortnightly') {
          <div class="editor-week-cards">
            @for (selector of weekSelectors; track selector.value) {
              <button class="editor-week-card" type="button" [class.active]="($calendar().weekSelector ?? 'A') === selector.value" [disabled]="$readonly()" (click)="onWeekSelector(selector.value)">
                <span class="editor-week-card-title">{{ selector.label }}</span>
                <span class="editor-week-card-note">{{ weekSelectorNote(selector) }}</span>
              </button>
            }
          </div>
          <p class="editor-note">ISO options follow the printed calendar's week numbers (government contracts); A/B keep a strict 14-day rhythm.</p>
        }

        <!-- Picked months override the interval in compile(), so the stepper disappears rather than
             sitting there claiming a cadence the rule does not carry. -->
        @if ($showMonthIntervalRow()) {
          <div class="editor-inline">
            <span class="editor-muted">Every</span>
            <wb-number-stepper label="Month interval" [value]="$calendar().monthInterval ?? 3" [min]="2" [max]="6" [disabled]="$readonly()" (valueChange)="onMonthInterval($event)" />
            <span class="editor-muted">months</span>
            @if ($quarterlyAnchors()) {
              <span class="editor-muted">· lands in {{ $quarterlyAnchors() }}</span>
            }
          </div>
        } @else if ($fixedMonthsNote()) {
          <div class="editor-inline">
            <span class="editor-muted">{{ $fixedMonthsNote() }}</span>
          </div>
        }

        @if ($showMonthDayBlock()) {
          <div class="editor-radio-rows">
            <wb-button-radio label="Day of the month" [checked]="($calendar().monthlyOn ?? 'dayOfMonth') === 'dayOfMonth'" [disabled]="$readonly()" (onToggle)="onMonthlyOn('dayOfMonth')" />
            @if (($calendar().monthlyOn ?? 'dayOfMonth') === 'dayOfMonth') {
              <wb-day-of-month-grid [value]="$calendar().daysOfMonth ?? []" [disabled]="$readonly()" (valueChange)="onDaysOfMonth($event)" />
            }

            <wb-button-radio label="A weekday of the month" [checked]="$calendar().monthlyOn === 'nthWeekday'" [disabled]="$readonly()" (onToggle)="onMonthlyOn('nthWeekday')" />
            @if ($calendar().monthlyOn === 'nthWeekday') {
              <div class="editor-ordinals">
                @for (ordinal of ordinalOptions; track ordinal._id) {
                  <wb-button-toggle displayStyle="Primary" [label]="ordinal.label" [checked]="isNthSelected(ordinal.data)" [disabled]="$readonly()" (onCheck)="onNthToggle(ordinal.data)" />
                }
              </div>
              <wb-button-dropdown class="editor-dropdown btn-field" [commands]="weekdayOptions" [activeCommandId]="$weekdayId()" [disabled]="$readonly()" menuAlignment="Left" (onCommandChanged)="onWeekday($event.data)" />
            }
          </div>
        }
      </section>

      <section class="editor-block">
        <div class="editor-section-head">
          <span class="editor-label">Months</span>
          @if ($showSeasonalToggle()) {
            <button class="editor-link" type="button" [disabled]="$readonly()" (click)="onSeasonalToggle()">{{ $seasonalOpen() ? 'All year' : 'Only in some months' }}</button>
          }
        </div>

        @if ($seasonalOpen()) {
          <div class="editor-month-rows">
            @for (row of monthChipRows; track $index) {
              <div class="editor-month-row">
                @for (month of row; track month.month) {
                  <wb-button-toggle displayStyle="Primary" [label]="month.label" [checked]="isMonthSelected(month.month)" [disabled]="$readonly()" (onCheck)="onMonthToggle(month.month)" />
                }
              </div>
            }
          </div>
        }
      </section>

      <section class="editor-block">
        <span class="editor-label">Ends</span>
        <wb-button-tab class="editor-ends" [commands]="$endsOptions()" [activeCommandId]="$endsType()" [fullWidth]="true" (onCommandChanged)="onEndsType($event.data)" />

        @if ($endsType() === 'onDate') {
          <wb-mini-calendar [value]="$endsDate() || null" (valueChange)="onEndsDate($event)" />
        }

        @if ($endsType() === 'afterCount') {
          <div class="editor-inline">
            <wb-number-stepper label="Service count" [value]="$endsCount()" [min]="1" [disabled]="$readonly()" (valueChange)="onEndsCount($event)" />
            <span class="editor-muted">{{ $endsCountNote() }}</span>
          </div>
        }
      </section>

      <section class="editor-block">
        <span class="editor-label">If it lands on a weekend or holiday</span>
        <wb-button-dropdown class="editor-dropdown btn-field" [commands]="nonWorkingDayOptions" [activeCommandId]="$calendar().nonWorkingDayPolicy" [disabled]="$readonly()" menuAlignment="Left" (onCommandChanged)="onNonWorkingDayPolicy($event.data)" />
      </section>
    }

    <!-- The list IS the schedule, so there is no Ends section here: the last date is the end. The
         non-working-day rule stays, because a hand-picked day can still land on a closure. -->
    @case ('dates') {
      <section class="editor-block">
        <span class="editor-label">Repeat</span>
        <wb-button-chip class="editor-repeats" [commands]="$repeatOptions()" [value]="$repeatSelection()" [disabled]="$readonly()" (onCommandChanged)="onRepeatChange($event.data)" />
      </section>

      <section class="editor-block">
        <span class="editor-label">On these dates</span>

        @if ($dateRows().length) {
          <ul class="editor-date-list">
            @for (row of $dateRows(); track row.date) {
              <li class="editor-date-row">
                <i class="editor-date-icon fa-solid fa-calendar-day" aria-hidden="true"></i>
                <span class="editor-date-label">{{ row.label }}</span>
                @if (!$readonly()) {
                  <button class="editor-date-remove" type="button" [attr.aria-label]="'Remove ' + row.label" (click)="onRemoveDate(row.date)">
                    <i class="fa-solid fa-xmark"></i>
                  </button>
                }
              </li>
            }
          </ul>
        }

        @if (!$readonly()) {
          <div class="editor-assembly">
            <button class="editor-link" type="button" [disabled]="$datesFull()" cdkOverlayOrigin #addDateOrigin="cdkOverlayOrigin" (click)="toggleAddDate()">+ Add date</button>
            <span class="editor-caption">{{ datesHint }}</span>
          </div>

          <ng-template cdkConnectedOverlay [cdkConnectedOverlayOrigin]="addDateOrigin" [cdkConnectedOverlayPositions]="firstDuePositions" [cdkConnectedOverlayOpen]="$addDateOpen()" [cdkConnectedOverlayHasBackdrop]="false" [cdkConnectedOverlayPush]="true" (overlayOutsideClick)="closeAddDate()" (detach)="closeAddDate()">
            <div class="editor-datepopover">
              <wb-mini-calendar [value]="null" [disabledDate]="isOutsideLine" (valueChange)="onAddDate($event)" />
            </div>
          </ng-template>
        }
      </section>

      <section class="editor-block">
        <span class="editor-label">If it lands on a weekend or holiday</span>
        <wb-button-dropdown class="editor-dropdown btn-field" [commands]="nonWorkingDayOptions" [activeCommandId]="$draft().dates.nonWorkingDayPolicy" [disabled]="$readonly()" menuAlignment="Left" (onCommandChanged)="onDatesNonWorkingDayPolicy($event.data)" />
      </section>
    }
  }

  <!-- On a template these dates are an illustration anchored on today, not a commitment: the label and
       the note below say so, because the numbers themselves look identical either way. -->
  <section class="editor-block">
    <span class="editor-label">{{ $previewLabel() }}</span>
    @if ($kind() === 'perVisit') {
      <p class="editor-muted">Does not create dates of its own</p>
    } @else if ($previewText()) {
      <p class="editor-preview-dates">{{ $previewText() }}</p>
      @if ($isTemplate()) {
        <span class="editor-caption">{{ templatePreviewNote }}</span>
      }
    } @else {
      <p class="editor-muted">No upcoming dates</p>
    }
  </section>
</div>

<footer class="editor-foot">
  <!-- Why Save is unavailable, said on screen. A greyed button with no reason leaves the user hunting
       the pane for the one control they have not finished. -->
  @if ($firstIssue() && !$readonly()) {
    <span class="editor-issue" role="alert">{{ $firstIssue() }}</span>
  } @else {
    <span class="editor-muted">{{ $footerNote() }}</span>
  }
  <div class="editor-actions">
    <wb-button label="Cancel" [size]="controlSize" (onClick)="cancel()" />
    @if (!$readonly()) {
      <wb-button label="Save" [displayStyle]="primary" [size]="controlSize" [disabled]="!$canSave()" (onClick)="save()" />
    }
  </div>
</footer>
